import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, authFileUrl } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Warehouse, Plus, Trash2, Pencil, Minus, ShoppingCart, History, PackagePlus, Upload, ClipboardList, Zap, GitMerge } from "lucide-react";

const STORES = [
  { key: "raw_wood", label: "Raw Wood Store" },
  { key: "sheet_board", label: "Ply / MDF / Veneer Store" },
  { key: "hardware", label: "Hardware Store" },
  { key: "tools_machinery", label: "Tools & Machinery Store" },
  { key: "misc", label: "Other Misc Store" },
];
const num = (x) => (parseFloat(x) || 0);
const empty = { store: "raw_wood", name: "", unit: "nos", quantity: 0, min_qty: 0, note: "", item_type: "", length: "", thickness: "", image: "" };
const HARDWARE_TYPES = ["Screw", "Hinge", "D-nut", "Fevicol/Adhesive", "Handle", "Lock", "Nail", "Bracket", "Other"];

export default function Stores() {
  const { user } = useAuth();
  const canManage = ["admin", "manager", "store_keeper"].includes(user?.role);
  const [store, setStore] = useState("raw_wood");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const canOrder = ["admin", "manager", "store_keeper"].includes(user?.role);
  const [vendors, setVendors] = useState([]);
  const [reorder, setReorder] = useState(null);           // item being reordered
  const [rForm, setRForm] = useState({ vendor_id: "", qty: "", rate: "" });
  const [logItem, setLogItem] = useState(null);           // item whose log is open
  const [logs, setLogs] = useState([]);
  const [pending, setPending] = useState([]);
  const [recv, setRecv] = useState(null);
  const [recvForm, setRecvForm] = useState(null);
  const [suppliers, setSuppliers] = useState({});   // {name_lower: {vendor_id, vendor_name, rate}}
  const [allotments, setAllotments] = useState([]);
  const [allot, setAllot] = useState(null);          // allotment being fulfilled
  const [allotQty, setAllotQty] = useState("");
  const [openPos, setOpenPos] = useState([]);        // open purchase orders for receipt matching
  const isAdmin = user?.role === "admin";
  const [headDlg, setHeadDlg] = useState(null);      // { from, mode: "rename"|"merge", to }
  const [headBusy, setHeadBusy] = useState(false);
  const submitHead = async () => {
    const to = (headDlg.to || "").trim();
    if (!to) return toast.error("Enter a head name");
    setHeadBusy(true);
    try {
      const r = await api.post("/stock/heads/merge", { store, from_name: headDlg.from, to_name: to });
      toast.success(`Head "${headDlg.from}" → "${r.data.to_name}" (${r.data.merged} rows consolidated)`);
      setHeadDlg(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    setHeadBusy(false);
  };

  const load = () => api.get("/stock").then((r) => setItems(r.data));
  const loadPending = () => api.get("/stock/pending-receipts").then((r) => setPending(r.data || [])).catch(() => {});
  const loadAllotments = () => api.get("/allotments?status=open").then((r) => setAllotments(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    loadPending();
    loadAllotments();
    api.get("/ledgers?type=vendor").then((r) => setVendors(r.data || []));
    api.get("/stock/last-suppliers").then((r) => setSuppliers(r.data || {})).catch(() => {});
    api.get("/purchase-orders/open").then((r) => setOpenPos(r.data || [])).catch(() => {});
  }, []);

  const uploadImg = async (file, cb) => {
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd); cb(data.path); toast.success("Photo added"); }
    catch (e) { toast.error("Upload failed"); }
  };
  const guessStore = (g) => {
    const t = (g.item || "").toLowerCase();
    if (/ply|mdf|veneer|board/.test(t)) return "sheet_board";
    if (/screw|hinge|nail|handle|lock|bracket|hardware|d-nut|fevicol|adhesive/.test(t)) return "hardware";
    if (/wood|timber|plank|mango|sheesham|teak|pine/.test(t)) return "raw_wood";
    return "misc";
  };
  const openReceive = (g, line, idx) => {
    const ln = line || { item: g.item, quantity: g.quantity, unit: g.unit };
    const store = guessStore({ item: ln.item });
    let name = ln.item || "";
    if (store === "raw_wood") {
      const rwSpecies = [...new Set(items.filter((x) => x.store === "raw_wood").map((x) => (x.name || "").trim()).filter(Boolean))];
      const m = rwSpecies.find((s) => (ln.item || "").toLowerCase().includes(s.toLowerCase()));
      name = m || "";   // preselect matched species head; else force manual pick from the list
    }
    setRecv(g);
    setRecvForm({ stock_item_id: "", store, name, unit: ln.unit || "nos",
      quantity: String(ln.quantity || ""), min_qty: "0", item_type: "", length: "", thickness: "",
      image: (g.photos && g.photos[0]) || g.attachment || "", note: `From gate: ${g.party || ""}`,
      po_id: "", po_line_index: -1, gate_line_index: (idx != null ? idx : -1) });
  };
  const submitReceive = async () => {
    try {
      const { data } = await api.post("/stock/receive", { gate_id: recv.id, ...recvForm, quantity: num(recvForm.quantity), min_qty: num(recvForm.min_qty) });
      if (data?.po_matched?.warning) toast.warning(`Posted to stock · ${data.po_matched.po_number}: ${data.po_matched.warning}`);
      else toast.success(data?.po_matched ? `Posted to stock · matched ${data.po_matched.po_number} (${data.po_matched.status})` : "Posted to stock");
      setRecv(null); load(); loadPending();
      api.get("/purchase-orders/open").then((r) => setOpenPos(r.data || [])).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const supplierFor = (it) => suppliers[(it.name || "").trim().toLowerCase()] || null;
  const oneTapReorder = async (it) => {
    try {
      const { data } = await api.post(`/stock/${it.id}/reorder`, {});
      toast.success(`Draft PO ${data?.po_number || ""} → ${data?.buyer_name || "supplier"} — see Purchase Orders`);
      refreshSuppliers();
    } catch (e) {
      if (e.response?.status === 400) { openReorder(it); }
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const refreshSuppliers = () => api.get("/stock/last-suppliers").then((r) => setSuppliers(r.data || {})).catch(() => {});

  const openReorder = (it) => {
    const sup = supplierFor(it);
    const suggest = Math.max(num(it.min_qty) * 2 - num(it.quantity), num(it.min_qty) || 1);
    setRForm({ vendor_id: sup?.vendor_id || "", qty: String(suggest), rate: sup?.rate ? String(sup.rate) : "" });
    setReorder(it);
  };
  const submitReorder = async () => {
    try {
      const { data } = await api.post(`/stock/${reorder.id}/reorder`, { vendor_id: rForm.vendor_id, qty: num(rForm.qty), rate: num(rForm.rate) });
      toast.success(`Draft PO ${data?.so_number || data?.po_number || ""} created — see Purchase Orders`);
      setReorder(null); refreshSuppliers();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openAllot = (a) => {
    const remaining = Math.max(0, num(a.required_qty) - num(a.allotted_qty) - num(a.shortage_qty));
    setAllot(a); setAllotQty(String(remaining));
  };
  const submitAllot = async () => {
    try {
      const { data } = await api.post(`/allotments/${allot.id}/allot`, { qty: num(allotQty) });
      if (data?.shortage_po) toast.warning(`Issued ${data.issued}; short ${data.shortfall} → draft ${data.shortage_po.po_number} created`);
      else toast.success(`Issued ${data.issued} ${allot.unit}`);
      setAllot(null); load(); loadAllotments();
      api.get("/purchase-orders/open").then((r) => setOpenPos(r.data || [])).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openLog = async (it) => {
    setLogItem(it); setLogs([]);
    try { const r = await api.get(`/stock/${it.id}/log`); setLogs(r.data); }
    catch (e) { toast.error("Could not load history"); }
  };

  const rows = items.filter((i) => i.store === store);
  const lowCount = rows.filter((i) => num(i.quantity) <= num(i.min_qty)).length;

  const openNew = () => { setEditing(null); setForm({ ...empty, store }); setOpen(true); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, quantity: num(form.quantity), min_qty: num(form.min_qty) };
    try {
      if (editing) await api.put(`/stock/${editing}`, payload);
      else await api.post("/stock", payload);
      toast.success("Stock item saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const adjust = async (it, delta) => {
    try { await api.patch(`/stock/${it.id}/qty`, { delta }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this stock item and its history? This cannot be undone.")) return;
    try { await api.delete(`/stock/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Stores" icon={Warehouse}
        subtitle="Stock on hand across your stores — quantities available and low-stock alerts."
        action={canManage && (
          <Button data-testid="add-stock-button" onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> New Item</Button>
        )} />

      <div className="flex flex-wrap gap-2">
        {STORES.map((s) => {
          const c = items.filter((i) => i.store === s.key).length;
          return (
            <button key={s.key} data-testid={`store-tab-${s.key}`} onClick={() => setStore(s.key)}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${store === s.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/50"}`}>
              {s.label} <span className="ml-1 text-xs opacity-70">({c})</span>
            </button>
          );
        })}
      </div>

      {lowCount > 0 && (
        <p className="text-sm text-destructive" data-testid="low-stock-note">⚠ {lowCount} item(s) at or below minimum stock in this store.</p>
      )}

      {canManage && pending.length > 0 && (
        <Card className="rounded-md border-ochre/40 bg-ochre/5 p-4 shadow-none" data-testid="pending-receipts">
          <div className="mb-2 flex items-center gap-2"><PackagePlus className="h-4 w-4 text-ochre" /><p className="font-heading text-sm font-bold">{pending.length} gate inward{pending.length > 1 ? "s" : ""} awaiting posting to stock</p></div>
          <div className="space-y-2">
            {pending.map((g) => {
              const glines = (g.lines && g.lines.length) ? g.lines : [{ item: g.item, quantity: g.quantity, unit: g.unit }];
              const recd = new Set(g.received_lines || []);
              const openLines = glines.map((ln, idx) => ({ ln, idx })).filter((x) => !recd.has(x.idx));
              if (openLines.length === 0) return null;
              return (
                <div key={g.id} data-testid={`receipt-${g.id}`} className="rounded-md border border-border bg-background px-3 py-2 text-sm">
                  <div className="mb-1.5 flex items-center gap-2">
                    {((g.photos && g.photos[0]) || g.attachment) && <img src={authFileUrl((g.photos && g.photos[0]) || g.attachment)} alt="" className="h-9 w-9 rounded border border-border object-cover" />}
                    <p className="text-xs text-muted-foreground">{g.party || "—"} · {(g.doc_type || "")} {(g.doc_no || "")} · {(g.entry_date || g.created_at || "").slice(0, 10)}{glines.length > 1 ? ` · ${openLines.length}/${glines.length} line(s) pending` : ""}</p>
                  </div>
                  <div className="space-y-1">
                    {openLines.map(({ ln, idx }) => (
                      <div key={idx} data-testid={`receipt-line-${g.id}-${idx}`} className="flex flex-wrap items-center justify-between gap-2 rounded border border-border/60 px-2 py-1">
                        <span className="font-medium">{ln.item || "—"} <span className="text-muted-foreground">· {ln.quantity || 0} {ln.unit || ""}</span></span>
                        <Button size="sm" data-testid={`receive-btn-${g.id}-${idx}`} onClick={() => openReceive(g, ln, idx)} className="h-7 gap-1 text-xs"><PackagePlus className="h-3.5 w-3.5" /> Receive</Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {canManage && allotments.length > 0 && (
        <Card className="rounded-md border-primary/30 bg-primary/5 p-4 shadow-none" data-testid="allotment-queue">
          <div className="mb-3 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" /><p className="font-heading text-sm font-bold">Contractor Allotment Queue · {allotments.length} open line(s)</p></div>
          <div className="space-y-3">
            {Object.entries(allotments.reduce((acc, a) => { (acc[a.store_label] = acc[a.store_label] || []).push(a); return acc; }, {})).map(([label, lines]) => (
              <div key={label}>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-primary">{label}</p>
                <div className="space-y-2">
                  {lines.map((a) => {
                    const remaining = Math.max(0, num(a.required_qty) - num(a.allotted_qty) - num(a.shortage_qty));
                    return (
                      <div key={a.id} data-testid={`allot-${a.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
                        <div>
                          <p className="font-medium">{a.name} <span className="text-xs text-muted-foreground">· {a.so_number} → {a.party}</span></p>
                          <p className="text-xs text-muted-foreground">
                            Need {a.required_qty} {a.unit} · Allotted {a.allotted_qty}
                            {num(a.shortage_qty) > 0 && <span className="text-destructive"> · Short {a.shortage_qty}{a.shortage_po_number ? ` (draft ${a.shortage_po_number})` : ""}</span>}
                            {" · "}<span className={a.status === "partial" ? "text-ochre font-semibold" : ""}>{a.status}</span>
                          </p>
                        </div>
                        <Button size="sm" data-testid={`allot-btn-${a.id}`} onClick={() => openAllot(a)} disabled={remaining <= 0} className="gap-1">
                          <PackagePlus className="h-3.5 w-3.5" /> Allot {remaining > 0 ? remaining : ""}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {canOrder && rows.some((i) => num(i.quantity) <= num(i.min_qty)) && (
        <Card className="rounded-md border-destructive/30 bg-destructive/5 p-4 shadow-none" data-testid="low-stock-reorder">
          <div className="mb-3 flex items-center gap-2"><Zap className="h-4 w-4 text-destructive" /><p className="font-heading text-sm font-bold">Low Stock — one-tap reorder</p></div>
          <div className="space-y-2">
            {rows.filter((i) => num(i.quantity) <= num(i.min_qty)).map((it) => {
              const sup = supplierFor(it);
              return (
                <div key={it.id} data-testid={`lowstock-${it.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium">{it.name} <span className="text-xs text-destructive">· {it.quantity} {it.unit} (min {it.min_qty})</span></p>
                    <p className="text-xs text-muted-foreground">{sup ? `Usual supplier: ${sup.vendor_name}${sup.rate ? ` @ ₹${sup.rate}` : ""}` : "No usual supplier yet — pick one"}</p>
                  </div>
                  <Button size="sm" variant={sup ? "default" : "outline"} data-testid={`lowstock-reorder-${it.id}`} onClick={() => oneTapReorder(it)} className="gap-1">
                    <ShoppingCart className="h-3.5 w-3.5" /> {sup ? "Draft PO" : "Reorder…"}
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {rows.length === 0 ? (
        <EmptyState icon={Warehouse} title="No items in this store" hint="Add an item to track its stock." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Item</TableHead>
                {store === "raw_wood" && <><TableHead>Length</TableHead><TableHead>Thickness</TableHead></>}
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Available</TableHead><TableHead className="text-right">Min</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const groupOf = (it) => store === "raw_wood" ? (it.name || "—") : store === "hardware" ? (it.item_type || "Other") : "";
                const showGroup = store === "raw_wood" || store === "hardware";
                const colspan = store === "raw_wood" ? 8 : 6;
                const groups = {};
                rows.forEach((it) => { (groups[groupOf(it)] = groups[groupOf(it)] || []).push(it); });
                return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])).map(([g, its]) => (
                  <React.Fragment key={g || "flat"}>
                    {showGroup && <TableRow className="bg-muted/30 hover:bg-muted/30"><TableCell colSpan={colspan} className="py-1.5 text-xs font-bold uppercase tracking-wide text-primary" data-testid={`stock-group-${g}`}><div className="flex items-center justify-between">
                      <span>{g} <span className="font-normal text-muted-foreground">({its.length})</span></span>
                      {isAdmin && store === "raw_wood" && <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px] normal-case tracking-normal text-muted-foreground hover:text-primary" data-testid={`head-merge-${g}`} onClick={() => setHeadDlg({ from: g, mode: "rename", to: g })}><GitMerge className="h-3 w-3" /> Rename / Merge</Button>}
                    </div></TableCell></TableRow>}
                    {its.map((it) => {
                      const low = num(it.quantity) <= num(it.min_qty);
                      return (
                        <TableRow key={it.id} data-testid={`stock-row-${it.id}`} className="hover:bg-muted/40">
                          <TableCell className="font-semibold"><div className="flex items-center gap-2">{it.image && <img src={authFileUrl(it.image)} alt="" className="h-8 w-8 rounded border border-border object-cover" />}{it.name}</div></TableCell>
                          {store === "raw_wood" && <><TableCell className="text-muted-foreground">{it.length || "—"}</TableCell><TableCell className="text-muted-foreground">{it.thickness || "—"}</TableCell></>}
                          <TableCell className="text-muted-foreground">{it.unit}</TableCell>
                          <TableCell className="text-right font-heading font-bold" data-testid={`stock-qty-${it.id}`}>{it.quantity}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{it.min_qty}</TableCell>
                          <TableCell>{low ? <Badge variant="destructive">Low</Badge> : <Badge className="bg-sage/20 text-sage hover:bg-sage/20">OK</Badge>}</TableCell>
                          <TableCell className="text-right">
                            {canManage && (
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`stock-minus-${it.id}`} onClick={() => adjust(it, -1)}><Minus className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`stock-plus-${it.id}`} onClick={() => adjust(it, 1)}><Plus className="h-4 w-4" /></Button>
                                {canOrder && <Button variant="ghost" size="icon" className={`h-8 w-8 ${low ? "text-destructive hover:text-destructive" : "text-muted-foreground hover:text-primary"}`} title="Reorder from supplier" data-testid={`stock-reorder-${it.id}`} onClick={() => openReorder(it)}><ShoppingCart className="h-4 w-4" /></Button>}
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" title="Stock history" data-testid={`stock-log-${it.id}`} onClick={() => openLog(it)}><History className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" data-testid={`stock-edit-${it.id}`} onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`stock-delete-${it.id}`} onClick={() => remove(it.id)}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ));
              })()}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!headDlg} onOpenChange={(o) => !o && setHeadDlg(null)}>
        <DialogContent data-testid="head-merge-dialog">
          <DialogHeader><DialogTitle className="font-heading">Timber head: {headDlg?.from}</DialogTitle></DialogHeader>
          {headDlg && (() => {
            const others = [...new Set(items.filter((i) => i.store === store && i.name && i.name !== headDlg.from).map((i) => i.name))].sort();
            return (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={headDlg.mode === "rename" ? "default" : "outline"} data-testid="head-mode-rename" onClick={() => setHeadDlg({ ...headDlg, mode: "rename", to: headDlg.from })}>Rename</Button>
                  <Button type="button" size="sm" variant={headDlg.mode === "merge" ? "default" : "outline"} data-testid="head-mode-merge" onClick={() => setHeadDlg({ ...headDlg, mode: "merge", to: "" })}>Merge into another head</Button>
                </div>
                {headDlg.mode === "rename" ? (
                  <div className="space-y-2"><Label>New head name</Label>
                    <Input data-testid="head-new-name" value={headDlg.to} onChange={(e) => setHeadDlg({ ...headDlg, to: e.target.value })} /></div>
                ) : (
                  <div className="space-y-2"><Label>Merge into</Label>
                    <select data-testid="head-target" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={headDlg.to} onChange={(e) => setHeadDlg({ ...headDlg, to: e.target.value })}>
                      <option value="">Select head…</option>
                      {others.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select></div>
                )}
                <p className="text-xs text-muted-foreground">All items under this head move to the new name. Rows with the same length, thickness and unit are combined and their quantities added; stock history is preserved.</p>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHeadDlg(null)}>Cancel</Button>
            <Button data-testid="head-submit" disabled={headBusy} onClick={submitHead}>{headDlg?.mode === "merge" ? "Merge" : "Rename"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} Stock Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Store</Label>
              <select data-testid="stock-store" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.store} onChange={(e) => setForm({ ...form, store: e.target.value })}>
                {STORES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select></div>
            <div className="space-y-2"><Label>Item name</Label>
              <Input data-testid="stock-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.store === "raw_wood" ? "e.g. Mango" : "e.g. Mango Wood Plank"} /></div>
            {form.store === "hardware" && (
              <div className="space-y-2"><Label>Type</Label>
                <Select value={HARDWARE_TYPES.includes(form.item_type) ? form.item_type : (form.item_type ? "__custom" : "")} onValueChange={(v) => setForm({ ...form, item_type: v === "__custom" ? "" : v })}>
                  <SelectTrigger data-testid="stock-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{HARDWARE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}<SelectItem value="__custom">Custom…</SelectItem></SelectContent>
                </Select>
                {!HARDWARE_TYPES.includes(form.item_type) && <Input className="mt-1" data-testid="stock-type-custom" value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} placeholder="Type a custom type" />}
              </div>
            )}
            {form.store === "raw_wood" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Length</Label>
                  <Input data-testid="stock-length" value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} placeholder="e.g. 6'" /></div>
                <div className="space-y-2"><Label>Thickness</Label>
                  <Input data-testid="stock-thickness" value={form.thickness} onChange={(e) => setForm({ ...form, thickness: e.target.value })} placeholder={"e.g. 1.5\""} /></div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Unit</Label>
                <Input data-testid="stock-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder={form.store === "raw_wood" ? "bft (board-feet)" : "nos / cft / ltr"} /></div>
              <div className="space-y-2"><Label>Available qty</Label>
                <Input data-testid="stock-quantity" type="text" inputMode="decimal" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div className="space-y-2"><Label>Min qty</Label>
                <Input data-testid="stock-min" type="text" inputMode="decimal" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Note</Label>
              <Input data-testid="stock-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <div className="space-y-2"><Label>Photo</Label>
              {form.image ? (
                <div className="flex items-center gap-2"><img src={authFileUrl(form.image)} alt="" className="h-14 w-14 rounded border border-border object-cover" /><Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, image: "" })}>Remove</Button></div>
              ) : (
                <label className="flex w-fit cursor-pointer items-center gap-1 rounded-md border border-dashed border-primary/50 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/5"><Upload className="h-3.5 w-3.5" /> Upload photo
                  <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="stock-image-upload" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0], (p) => setForm((f) => ({ ...f, image: p })))} /></label>
              )}
            </div>
          </div>
          <DialogFooter><Button data-testid="stock-save" onClick={save} disabled={!form.name}>Save Item</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reorder} onOpenChange={(v) => !v && setReorder(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Reorder — {reorder?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Creates a <b>draft Purchase Order</b> under Purchase Orders. Available now: {reorder?.quantity} {reorder?.unit} (min {reorder?.min_qty}).</p>
            <div className="space-y-2"><Label>Supplier / Vendor</Label>
              <Select value={rForm.vendor_id} onValueChange={(v) => setRForm({ ...rForm, vendor_id: v })}>
                <SelectTrigger data-testid="reorder-vendor"><SelectValue placeholder="Select vendor from ledgers" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
              {vendors.length === 0 && <p className="text-xs text-destructive">No vendors yet — add one under Ledgers.</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Quantity to order</Label>
                <Input data-testid="reorder-qty" type="text" inputMode="decimal" value={rForm.qty} onChange={(e) => setRForm({ ...rForm, qty: e.target.value })} /></div>
              <div className="space-y-2"><Label>Rate (optional)</Label>
                <Input data-testid="reorder-rate" type="text" inputMode="decimal" value={rForm.rate} onChange={(e) => setRForm({ ...rForm, rate: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="reorder-submit" onClick={submitReorder} disabled={!rForm.vendor_id || num(rForm.qty) <= 0}>Create Draft Order</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recv} onOpenChange={(v) => !v && setRecv(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Receive to stock</DialogTitle></DialogHeader>
          {recvForm && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">From gate: <b>{recv?.party || "—"}</b> · {(recv?.doc_type || "")} {(recv?.doc_no || "")} · {(recv?.entry_date || recv?.created_at || "").slice(0, 10)}</p>
              <div className="space-y-2"><Label>Store</Label>
                <select data-testid="recv-store" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={recvForm.store} onChange={(e) => setRecvForm({ ...recvForm, store: e.target.value, stock_item_id: "" })}>{STORES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
              {recvForm.store === "raw_wood" ? (() => {
                const rw = items.filter((x) => x.store === "raw_wood");
                const species = [...new Set(rw.map((x) => (x.name || "").trim()).filter(Boolean))].sort();
                const speciesVal = recvForm.stock_item_id
                  ? (rw.find((x) => x.id === recvForm.stock_item_id)?.name || "")
                  : (recvForm.name && species.includes(recvForm.name) ? recvForm.name : (recvForm.name ? "__new" : ""));
                const lots = speciesVal && speciesVal !== "__new" ? rw.filter((x) => (x.name || "") === speciesVal) : [];
                return (<>
                  <div className="space-y-2"><Label>Timber species (stock head)</Label>
                    <Select value={speciesVal} onValueChange={(v) => {
                      if (v === "__new") setRecvForm({ ...recvForm, name: "", stock_item_id: "", length: "", thickness: "" });
                      else setRecvForm({ ...recvForm, name: v, stock_item_id: "", length: "", thickness: "" });
                    }}>
                      <SelectTrigger data-testid="recv-species"><SelectValue placeholder="Select existing species" /></SelectTrigger>
                      <SelectContent>
                        {species.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        <SelectItem value="__new">➕ New species…</SelectItem>
                      </SelectContent>
                    </Select>
                    {speciesVal === "__new" && <Input className="mt-1" data-testid="recv-species-new" value={recvForm.name} onChange={(e) => setRecvForm({ ...recvForm, name: e.target.value })} placeholder="New species e.g. Rosewood" />}
                    {!speciesVal && <p className="text-xs text-muted-foreground">Pick the species so it adds to the right stock head (not a new one each time).</p>}
                  </div>
                  {speciesVal && speciesVal !== "__new" && (
                    <div className="space-y-2"><Label>Lot — length × thickness</Label>
                      <Select value={recvForm.stock_item_id || "__newlot"} onValueChange={(v) => {
                        if (v === "__newlot") setRecvForm({ ...recvForm, stock_item_id: "", length: "", thickness: "" });
                        else { const it = rw.find((x) => x.id === v); setRecvForm({ ...recvForm, stock_item_id: v, name: it?.name || recvForm.name, unit: it?.unit || recvForm.unit, length: it?.length || "", thickness: it?.thickness || "" }); }
                      }}>
                        <SelectTrigger data-testid="recv-lot"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {lots.map((l) => <SelectItem key={l.id} value={l.id}>{(l.length || "?")} × {(l.thickness || "?")} · on hand {l.quantity} {l.unit}</SelectItem>)}
                          <SelectItem value="__newlot">➕ New lot (new size)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Qty received</Label><Input data-testid="recv-qty" type="text" inputMode="decimal" value={recvForm.quantity} onChange={(e) => setRecvForm({ ...recvForm, quantity: e.target.value })} /></div>
                    {!recvForm.stock_item_id && <div className="space-y-2"><Label>Unit</Label><Input data-testid="recv-unit" value={recvForm.unit} onChange={(e) => setRecvForm({ ...recvForm, unit: e.target.value })} placeholder="bft" /></div>}
                  </div>
                  {!recvForm.stock_item_id && (
                    <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>Length</Label><Input data-testid="recv-length" value={recvForm.length} onChange={(e) => setRecvForm({ ...recvForm, length: e.target.value })} placeholder="6'" /></div><div className="space-y-2"><Label>Thickness</Label><Input data-testid="recv-thickness" value={recvForm.thickness} onChange={(e) => setRecvForm({ ...recvForm, thickness: e.target.value })} placeholder={"1.5\""} /></div></div>
                  )}
                  {!recvForm.stock_item_id && (
                    <div className="space-y-2"><Label>Min qty</Label><Input data-testid="recv-min" type="text" inputMode="decimal" value={recvForm.min_qty} onChange={(e) => setRecvForm({ ...recvForm, min_qty: e.target.value })} /></div>
                  )}
                </>);
              })() : (<>
                <div className="space-y-2"><Label>Add to existing item (or create new)</Label>
                  <Select value={recvForm.stock_item_id || "__new"} onValueChange={(v) => { if (v === "__new") { setRecvForm({ ...recvForm, stock_item_id: "" }); } else { const it = items.find((x) => x.id === v); setRecvForm({ ...recvForm, stock_item_id: v, name: it?.name || recvForm.name, unit: it?.unit || recvForm.unit }); } }}>
                    <SelectTrigger data-testid="recv-existing"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="__new">➕ Create new item</SelectItem>{items.filter((x) => x.store === recvForm.store).map((x) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.quantity} {x.unit})</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Item name</Label><Input data-testid="recv-name" value={recvForm.name} onChange={(e) => setRecvForm({ ...recvForm, name: e.target.value })} disabled={!!recvForm.stock_item_id} /></div>
                  <div className="space-y-2"><Label>Qty received</Label><Input data-testid="recv-qty" type="text" inputMode="decimal" value={recvForm.quantity} onChange={(e) => setRecvForm({ ...recvForm, quantity: e.target.value })} /></div>
                </div>
                {!recvForm.stock_item_id && (
                  <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>Unit</Label><Input data-testid="recv-unit" value={recvForm.unit} onChange={(e) => setRecvForm({ ...recvForm, unit: e.target.value })} /></div><div className="space-y-2"><Label>Min qty</Label><Input data-testid="recv-min" type="text" inputMode="decimal" value={recvForm.min_qty} onChange={(e) => setRecvForm({ ...recvForm, min_qty: e.target.value })} /></div></div>
                )}
              </>)}
              <div className="space-y-2"><Label>Link to Purchase Order (received vs ordered)</Label>
                <Select value={recvForm.po_id || "__none"} onValueChange={(v) => { if (v === "__none") setRecvForm({ ...recvForm, po_id: "", po_line_index: -1 }); else setRecvForm({ ...recvForm, po_id: v, po_line_index: -1 }); }}>
                  <SelectTrigger data-testid="recv-po"><SelectValue placeholder="Optional — match an open PO" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No PO link</SelectItem>
                    {openPos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} · {p.buyer_name || "—"}</SelectItem>)}
                  </SelectContent>
                </Select>
                {recvForm.po_id && (() => {
                  const po = openPos.find((p) => p.id === recvForm.po_id);
                  if (!po) return null;
                  return (
                    <div className="mt-1 space-y-1 rounded-md border border-border p-2">
                      {po.lines.map((l) => (
                        <label key={l.index} className="flex items-center justify-between gap-2 text-xs" data-testid={`recv-po-line-${l.index}`}>
                          <span className="flex items-center gap-1">
                            <input type="radio" name="po-line" checked={recvForm.po_line_index === l.index} onChange={() => setRecvForm({ ...recvForm, po_line_index: l.index })} />
                            {l.name || "—"}
                          </span>
                          <span className="text-muted-foreground">recv {l.received}/{l.ordered} · {l.outstanding} left</span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div className="space-y-2"><Label>Photo</Label>
                {recvForm.image ? (
                  <div className="flex items-center gap-2"><img src={authFileUrl(recvForm.image)} alt="" className="h-14 w-14 rounded border border-border object-cover" /><Button type="button" variant="ghost" size="sm" onClick={() => setRecvForm({ ...recvForm, image: "" })}>Remove</Button></div>
                ) : (
                  <label className="flex w-fit cursor-pointer items-center gap-1 rounded-md border border-dashed border-primary/50 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/5"><Upload className="h-3.5 w-3.5" /> Upload photo
                    <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="recv-image-upload" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0], (p) => setRecvForm((f) => ({ ...f, image: p })))} /></label>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="recv-submit" onClick={submitReceive} disabled={!recvForm || num(recvForm?.quantity) <= 0 || (!recvForm?.stock_item_id && !recvForm?.name)}>Post to Stock</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!logItem} onOpenChange={(v) => !v && setLogItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Stock History — {logItem?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No history recorded yet.</p>
            ) : logs.map((l) => (
              <div key={l.id} data-testid={`stock-log-row-${l.id}`} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <div>
                  <p className="font-medium">{l.reason}</p>
                  <p className="text-xs text-muted-foreground">{new Date(l.at).toLocaleString()} · {l.by}</p>
                </div>
                <div className="text-right">
                  <p className={`font-heading font-bold ${l.delta < 0 ? "text-destructive" : "text-sage"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</p>
                  <p className="text-xs text-muted-foreground">→ {l.new_qty}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!allot} onOpenChange={(v) => !v && setAllot(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Allot — {allot?.name}</DialogTitle></DialogHeader>
          {allot && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{allot.so_number} → <b>{allot.party}</b> · from {allot.store_label}. Required {allot.required_qty} {allot.unit}, already allotted {allot.allotted_qty}. Issuing deducts stock; any shortfall auto-drafts a Purchase Order to the usual supplier.</p>
              <div className="space-y-2"><Label>Quantity to issue now</Label>
                <Input data-testid="allot-qty" type="text" inputMode="decimal" value={allotQty} onChange={(e) => setAllotQty(e.target.value)} /></div>
            </div>
          )}
          <DialogFooter><Button data-testid="allot-submit" onClick={submitAllot} disabled={num(allotQty) <= 0}>Issue / Allot</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
